/**
 * Inline formatting for article text.
 *
 * Blog content is stored as blocks of plain text with a small, familiar
 * markdown subset for inline styling:
 *
 *   **bold**            [label](https://example.com)
 *   *italic*
 *
 * This parses that into React elements. It deliberately does NOT use
 * `dangerouslySetInnerHTML`: the CMS is trusted, but building real elements
 * means a stray angle bracket in an article can never become markup, and a
 * pasted `javascript:` URL can never become a link. Safety by construction
 * rather than by sanitiser.
 *
 * A full WYSIWYG editor was the alternative. This keeps the stored content
 * portable, diffable and free of editor-specific HTML soup.
 */

/** Only allow schemes that make sense in article copy. */
const isSafeHref = (href) => /^(https?:\/\/|mailto:|tel:|\/)/i.test((href || "").trim());

const PATTERNS = [
  {
    // [label](url)
    re: /\[([^\]]+)\]\(([^)\s]+)\)/,
    render: (match, key) => {
      const href = match[2];
      if (!isSafeHref(href)) return <span key={key}>{match[1]}</span>;
      const external = /^https?:\/\//i.test(href);
      return (
        <a
          key={key}
          href={href}
          className="text-aqua underline underline-offset-2 hover:text-[#3FBAC4]"
          {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
        >
          {match[1]}
        </a>
      );
    },
  },
  {
    // **bold**
    re: /\*\*([^*]+)\*\*/,
    render: (match, key) => <strong key={key} className="font-semibold text-ink">{parseInline(match[1])}</strong>,
  },
  {
    // *italic*
    re: /\*([^*\n]+)\*/,
    render: (match, key) => <em key={key}>{parseInline(match[1])}</em>,
  },
];

let keySeed = 0;

/**
 * Turn one line of text into React children.
 * Returns a string when there is nothing to format, so the common case stays
 * cheap and React renders a plain text node.
 */
export function parseInline(text) {
  if (!text) return null;

  for (const pattern of PATTERNS) {
    const match = text.match(pattern.re);
    if (!match) continue;

    const before = text.slice(0, match.index);
    const after = text.slice(match.index + match[0].length);
    keySeed += 1;

    return (
      <>
        {before ? parseInline(before) : null}
        {pattern.render(match, `rt-${keySeed}`)}
        {after ? parseInline(after) : null}
      </>
    );
  }

  return text;
}

/** Split a block's text into lines, for list items. */
export const toLines = (text) =>
  String(text || "")
    .split(/\r?\n/)
    .map((line) => line.replace(/^\s*[-*]\s+/, "").replace(/^\s*\d+[.)]\s+/, "").trim())
    .filter(Boolean);

export default parseInline;
